<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter, RouterView } from 'vue-router'
import {
  Menu,
  X,
  LogOut,
  User,
  Sun,
  Moon,
  Monitor,
  UserRoundSearch,
  PanelLeftOpen
} from 'lucide-vue-next'
import {
  AppNavSearch,
  AppBreadcrumb,
  AppDropdown,
  AppConfirmDialog,
  AppGlobalLoader,
  Avatar,
  AvatarFallback,
  AvatarImage,
  AvatarBadge,
  useTheme,
  useAppToast,
  useNavigation,
  type DropdownItem
} from '@/shared'
import AppSidebarRail from './components/AppSidebarRail.vue'
import AppSidebarMenu from './components/AppSidebarMenu.vue'
import MessagingNotificationsBell from './components/MessagingNotificationsBell.vue'
import { useAuthStore } from '@/modules/auth'
import { PROFILE_ROUTE_NAME } from '@/modules/profile'
import { usePortalBrandingStore } from '@/shared/stores/portalBranding'
import { useGlobalLoadingStore } from '@/shared/stores/globalLoading'
import { storeToRefs } from 'pinia'
import { useAppStore } from '@/stores'
import { useGlobalMessagingNotifications } from '@/shared/composables/useGlobalMessagingNotifications'

const {
  activeModuleId,
  breadcrumbItems,
  handleModuleSelect: navHandleModuleSelect
} = useNavigation()
const appStore = useAppStore()
const { isSidebarOpen } = storeToRefs(appStore)
const route = useRoute()

/** Profile and Portal Settings are standalone pages (not a nav module) — hide the secondary menu by default. */
const isStandaloneRoute = computed(
  () =>
    route.name === PROFILE_ROUTE_NAME ||
    route.name === 'portal-settings' ||
    route.path === '/portal-settings'
)
/** Set when the user picks a rail module while still on a standalone page. */
const secondarySidebarForcedOpen = ref(false)
const isSecondarySidebarCollapsed = computed(
  () => !isSidebarOpen.value || (isStandaloneRoute.value && !secondarySidebarForcedOpen.value)
)

const handleModuleSelect = (id: string) => {
  navHandleModuleSelect(id)
  if (!appStore.isSidebarOpen) {
    appStore.isSidebarOpen = true
  }
  if (isStandaloneRoute.value) {
    secondarySidebarForcedOpen.value = true
  }
}

watch(isStandaloneRoute, (isStandalone) => {
  if (!isStandalone) {
    secondarySidebarForcedOpen.value = false
  }
})

const isMobileMenuOpen = ref(false)
const isSearchOpen = ref(false)
const showLeaveImpersonationDialog = ref(false)

const authStore = useAuthStore()
const globalLoading = useGlobalLoadingStore()
const branding = usePortalBrandingStore()
const router = useRouter()
const toast = useAppToast()

const { isEligible: showMessagingNotifications } = useGlobalMessagingNotifications()

// Close mobile nav only after navigation succeeds (not when blocked by unsaved-changes guard).
watch(
  () => router.currentRoute.value.fullPath,
  () => {
    if (isMobileMenuOpen.value) {
      isMobileMenuOpen.value = false
    }
  }
)

const handleSearch = () => {
  isSearchOpen.value = true
  // Close mobile menu if it's open
  if (isMobileMenuOpen.value) {
    isMobileMenuOpen.value = false
  }
}

const handleGlobalKeydown = (event: KeyboardEvent) => {
  if (
    event.key === '/' &&
    document.activeElement &&
    !['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement.tagName)
  ) {
    event.preventDefault()
    handleSearch()
  }
}

onMounted(() => {
  window.addEventListener('keydown', handleGlobalKeydown)
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleGlobalKeydown)
})

const toggleMobileMenu = () => {
  if (isSearchOpen.value) {
    isSearchOpen.value = false
  }
  isMobileMenuOpen.value = !isMobileMenuOpen.value
}

const closeMobileMenu = () => {
  if (isSearchOpen.value) {
    isSearchOpen.value = false
  }
  isMobileMenuOpen.value = false
}

const { mode } = useTheme()

const handleLogout = async () => {
  if (authStore.isLoggingOut) return
  try {
    await globalLoading.run(async () => {
      await authStore.logout()
      toast.success('Logged out', 'You have been successfully logged out.')
      await router.push({ name: 'login' })
    })
  } catch (error) {
    console.error('Logout error:', error)
  }
}

const handleLeaveImpersonation = () => {
  showLeaveImpersonationDialog.value = true
}

const confirmLeaveImpersonation = async () => {
  showLeaveImpersonationDialog.value = false
  try {
    await globalLoading.run(async () => {
      await router.push({ name: 'dashboard' })
      await authStore.leaveImpersonation()

      if (!authStore.token) {
        await router.push({ name: 'login' })
        return
      }
      if (!authStore.isImpersonating) {
        toast.success('Left Impersonation', 'You are now back in your original account.')
      }
    })
  } catch (error) {
    console.error('Leave impersonation error:', error)
    if (authStore.isImpersonating) {
      showLeaveImpersonationDialog.value = true
    }
  }
}

const navigateToPortalSettings = () => {
  router.push({ name: 'portal-settings' })
}

const navigateToProfile = () => {
  router.push({ name: 'profile' })
}

const userMenuItems = computed((): DropdownItem[] => {
  const items: DropdownItem[] = [
    { label: 'User Profile settings', icon: User, action: navigateToProfile }
  ]

  if (authStore.user?.role_enum === 'reseller-admin') {
    items.push({
      label: 'Portal Settings',
      icon: Monitor,
      action: navigateToPortalSettings
    })
  }

  items.push({ type: 'separator' })
  items.push({
    label: authStore.isLoggingOut ? 'Logging out...' : 'Log out',
    icon: LogOut,
    danger: true,
    disabled: authStore.isLoggingOut,
    loading: authStore.isLoggingOut,
    action: handleLogout
  })

  return items
})

const themeMenuItems: DropdownItem[] = [
  { label: 'Light', icon: Sun, action: () => (mode.value = 'light') },
  { label: 'Dark', icon: Moon, action: () => (mode.value = 'dark') },
  { label: 'System', icon: Monitor, action: () => (mode.value = 'auto') }
]
</script>

<template>
  <div class="relative flex h-screen w-full overflow-hidden bg-background">
    <!-- Search Nav Modal -->
    <AppNavSearch v-model:open="isSearchOpen" />
    <!-- Desktop Navigation (Hidden on Mobile) -->
    <AppSidebarRail
      class="hidden md:flex"
      :active-module-id="activeModuleId"
      :forced-collapsed="isSearchOpen"
      @select="handleModuleSelect"
      @search="handleSearch"
      @logout="handleLogout"
    />
    <AppSidebarMenu
      class="hidden md:flex"
      :active-module-id="activeModuleId"
      :collapsed="isSecondarySidebarCollapsed"
    />

    <!-- Mobile Navigation Overlay -->
    <div
      v-if="isMobileMenuOpen"
      class="fixed inset-0 z-[60] bg-black/50 transition-opacity md:hidden"
      @click="closeMobileMenu"
    />

    <div
      :class="[
        'fixed inset-y-0 left-0 z-[70] flex transition-transform duration-300 ease-in-out md:hidden',
        isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
      ]"
    >
      <AppSidebarRail
        :active-module-id="activeModuleId"
        :forced-collapsed="isSearchOpen"
        @select="handleModuleSelect"
        @search="handleSearch"
        @logout="handleLogout"
      />
      <AppSidebarMenu :active-module-id="activeModuleId" @close-mobile="closeMobileMenu" />
    </div>

    <!-- Main Content Area -->
    <div class="flex flex-1 flex-col overflow-hidden">
      <!-- Impersonation Banner -->
      <div
        v-if="authStore.isImpersonating"
        class="relative bg-warning/80 text-warning-foreground px-3 py-1.5 sm:px-4 sm:py-2 flex flex-row items-center justify-between sm:justify-center gap-2 sm:gap-6 text-xs sm:text-sm font-medium shrink-0 z-[40] shadow-md dark:shadow-none border-b border-warning/30 dark:bg-warning/10 dark:text-warning dark:border-warning/20 dark:backdrop-blur-sm"
      >
        <span
          aria-hidden="true"
          class="hidden dark:block absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-warning/60 to-transparent"
        />
        <div class="flex min-w-0 items-center gap-2 sm:gap-2.5">
          <div
            class="shrink-0 bg-warning-foreground/10 p-1 sm:p-1.5 rounded-full ring-1 ring-inset ring-warning-foreground/20"
          >
            <UserRoundSearch class="h-3 w-3" />
          </div>
          <div class="flex min-w-0 items-center gap-1.5 sm:gap-2">
            <div class="flex min-w-0 items-center gap-1.5 sm:gap-2">
              <span class="shrink-0 opacity-80 font-normal">Viewing as</span>
              <strong class="truncate font-bold tracking-wider">{{ authStore.user?.name }}</strong>
            </div>
            <div class="hidden sm:flex items-center gap-2">
              <span class="hidden md:inline opacity-40">|</span>
              <span
                class="bg-warning-foreground/10 tracking-wider px-2 py-0.5 rounded text-[10px] font-bold uppercase ring-1 ring-inset ring-warning-foreground/20"
              >
                {{ authStore.user?.role || 'User' }}
              </span>
              <span class="opacity-40">|</span>
              <span class="font-normal text-[11px] tracking-wider hidden lg:inline opacity-80">{{
                authStore.user?.email
              }}</span>
            </div>
          </div>
        </div>
        <button
          @click="handleLeaveImpersonation"
          class="shrink-0 bg-card text-warning dark:bg-warning/15 dark:text-warning px-2 py-1 sm:px-2.5 rounded-full transition-all duration-200 flex items-center gap-1 sm:gap-1.5 shadow-sm dark:shadow-none active:scale-95 text-[9px] sm:text-[10px] font-bold uppercase tracking-wider ring-1 ring-inset ring-warning/30"
        >
          <LogOut class="h-2.5 w-2.5" />
          <span class="sm:hidden">Stop</span>
          <span class="hidden sm:inline">Stop Impersonation</span>
        </button>
      </div>
      <!-- Mobile Header -->
      <header class="flex h-16 items-center justify-between border-b bg-card px-4 md:hidden">
        <div class="flex items-center gap-3">
          <button @click="toggleMobileMenu" class="rounded-lg p-2 hover:bg-accent">
            <Menu v-if="!isMobileMenuOpen" class="h-6 w-6 text-muted-foreground" />
            <X v-else class="h-6 w-6 text-muted-foreground" />
          </button>
          <!-- Compact Logo for Mobile Header -->
          <img
            :src="branding.logoIconWhiteUrl"
            :class="[
              'h-8 w-8 object-contain',
              branding.logoIconWhiteUrl.includes('logo-icon-white.png')
                ? 'invert dark:invert-0'
                : ''
            ]"
            :alt="branding.appLabel"
          />
        </div>
        <div class="flex items-center gap-2">
          <MessagingNotificationsBell v-if="showMessagingNotifications" compact />
          <!-- Mobile Theme Toggle -->
          <AppDropdown :items="themeMenuItems" width="w-40">
            <template #trigger>
              <button
                class="relative h-8 w-8 inline-flex items-center justify-center rounded-full bg-muted hover:bg-accent text-foreground transition-colors focus-visible:outline-none focus:ring-2 focus:ring-ring"
              >
                <Sun v-if="mode === 'light'" class="h-4 w-4" />
                <Moon v-else-if="mode === 'dark'" class="h-4 w-4" />
                <Monitor v-else class="h-4 w-4" />
              </button>
            </template>
          </AppDropdown>

          <!-- Mobile User Profile -->
          <AppDropdown :items="userMenuItems" width="w-56">
            <template #trigger>
              <button
                class="relative h-8 w-8 rounded-full bg-muted outline-none ring-offset-background transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <Avatar class="h-8 w-8">
                  <AvatarImage
                    :src="authStore.user?.avatar || 'https://github.com/shadcn.png'"
                    :alt="authStore.user?.name"
                  />
                  <AvatarFallback>{{
                    authStore.user?.name?.charAt(0).toUpperCase() || 'U'
                  }}</AvatarFallback>
                  <AvatarBadge />
                </Avatar>
              </button>
            </template>
            <template #header>
              <div
                class="relative flex flex-col items-center p-6 text-center bg-gradient-to-br from-primary/5 via-card to-primary/10 dark:from-muted/50 dark:via-card dark:to-muted/50"
              >
                <!-- Avatar with ring effect -->
                <div class="relative mb-3">
                  <div
                    class="absolute -inset-1 rounded-full bg-gradient-to-tr from-primary/20 to-primary/30 blur-sm"
                  ></div>
                  <Avatar class="h-13 w-13 border-2 border-card shadow-sm">
                    <AvatarImage
                      :src="authStore.user?.avatar || 'https://github.com/shadcn.png'"
                      :alt="authStore.user?.name"
                    />
                    <AvatarFallback class="bg-primary text-xl text-white">
                      {{ authStore.user?.name?.charAt(0).toUpperCase() || 'U' }}
                    </AvatarFallback>
                  </Avatar>
                </div>

                <div class="flex flex-col items-center">
                  <p class="text-base font-bold leading-tight text-foreground">
                    {{ authStore.user?.name }}
                  </p>
                  <p class="text-xs font-medium text-muted-foreground mt-1 mb-3">
                    {{ authStore.user?.email }}
                  </p>

                  <div
                    v-if="authStore.user?.reseller_name || authStore.user?.role"
                    class="flex mt-1"
                  >
                    <span
                      class="bg-primary/10 text-primary px-3 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider border border-primary/20 shadow-sm flex items-center gap-2"
                    >
                      <template v-if="authStore.user?.reseller_name">
                        <span>{{ authStore.user?.reseller_name }}</span>
                        <span class="opacity-30 font-light">|</span>
                      </template>
                      <span>{{ authStore.user?.role }}</span>
                    </span>
                  </div>
                </div>
              </div>
            </template>
          </AppDropdown>
        </div>
      </header>

      <!-- Desktop Header -->
      <header
        class="hidden h-16 items-center justify-between border-b bg-card px-8 md:flex shrink-0"
      >
        <div class="flex items-center gap-4 flex-1 overflow-hidden pr-4">
          <button
            v-if="isSecondarySidebarCollapsed && !isStandaloneRoute"
            type="button"
            @click="appStore.toggleSidebar()"
            class="shrink-0 rounded-lg p-2 text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
            title="Expand sidebar"
            aria-label="Expand sidebar"
          >
            <PanelLeftOpen class="h-5 w-5" />
          </button>
          <AppBreadcrumb :items="breadcrumbItems" />
        </div>

        <div class="flex items-center gap-4">
          <MessagingNotificationsBell v-if="showMessagingNotifications" />
          <!-- Desktop Theme Toggle -->
          <AppDropdown :items="themeMenuItems" width="w-40">
            <template #trigger>
              <button
                class="relative h-8 w-8 inline-flex items-center justify-center rounded-full bg-muted hover:bg-accent text-muted-foreground transition-colors focus-visible:outline-none focus:ring-2 focus:ring-ring"
              >
                <Sun v-if="mode === 'light'" class="h-4 w-4" />
                <Moon v-else-if="mode === 'dark'" class="h-4 w-4" />
                <Monitor v-else class="h-4 w-4" />
              </button>
            </template>
          </AppDropdown>

          <!-- Desktop User Profile -->
          <AppDropdown :items="userMenuItems" width="w-64">
            <template #trigger>
              <button
                class="group relative flex items-center gap-3 rounded-full py-1 pl-1 pr-3 transition-colors hover:bg-accent focus:outline-none"
              >
                <Avatar class="h-8 w-8 border border-border">
                  <AvatarImage src="https://github.com/shadcn.png" :alt="authStore.user?.name" />
                  <AvatarFallback class="bg-primary text-[10px] text-white">
                    {{ authStore.user?.name?.charAt(0).toUpperCase() || 'U' }}
                  </AvatarFallback>
                </Avatar>
                <div class="hidden flex-col items-start text-left lg:flex">
                  <div class="flex items-center gap-1.5">
                    <span class="text-xs font-bold text-foreground">{{
                      authStore.user?.name
                    }}</span>
                  </div>
                  <span class="text-[10px] text-muted-foreground font-medium">{{
                    authStore.user?.email
                  }}</span>
                </div>
              </button>
            </template>
            <template #header>
              <div
                class="relative flex flex-col items-center p-6 text-center bg-gradient-to-br from-primary/5 via-card to-primary/10 dark:from-muted/50 dark:via-card dark:to-muted/50"
              >
                <!-- Avatar with ring effect -->
                <div class="relative mb-3">
                  <div
                    class="absolute -inset-1 rounded-full bg-gradient-to-tr from-primary/20 to-primary/30 blur-sm"
                  ></div>
                  <Avatar class="h-16 w-16 border-2 border-card shadow-sm">
                    <AvatarImage
                      :src="authStore.user?.avatar || 'https://github.com/shadcn.png'"
                      :alt="authStore.user?.name"
                    />
                    <AvatarFallback class="bg-primary text-xl text-white">
                      {{ authStore.user?.name?.charAt(0).toUpperCase() || 'U' }}
                    </AvatarFallback>
                  </Avatar>
                </div>

                <div class="flex flex-col items-center">
                  <p class="text-base font-bold leading-tight text-foreground">
                    {{ authStore.user?.name }}
                  </p>
                  <p class="text-xs font-medium text-muted-foreground mt-1 mb-3">
                    {{ authStore.user?.email }}
                  </p>

                  <div
                    v-if="authStore.user?.reseller_name || authStore.user?.role"
                    class="flex mt-1"
                  >
                    <span
                      class="bg-primary/10 text-primary px-3 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider border border-primary/20 shadow-sm flex items-center gap-2"
                    >
                      <template v-if="authStore.user?.reseller_name">
                        <span>{{ authStore.user?.reseller_name }}</span>
                        <span class="opacity-30 font-light">|</span>
                      </template>
                      <span>{{ authStore.user?.role }}</span>
                    </span>
                  </div>
                </div>
              </div>
            </template>
          </AppDropdown>
        </div>
      </header>

      <!-- Mobile: legacy breadcrumb strip -->
      <div class="shrink-0 border-b border-border/50 bg-card px-4 py-2.5 md:hidden">
        <AppBreadcrumb :items="breadcrumbItems" />
      </div>

      <div class="flex min-h-0 flex-1 flex-col overflow-y-auto px-4 pt-4 md:px-8 md:pt-8">
        <div class="flex min-h-full flex-1 flex-col">
          <div class="flex-1">
            <RouterView />
          </div>

          <footer class="mt-auto shrink-0 pb-4 pt-8 text-xs text-muted-foreground">
            <div class="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <p>{{ branding.copyrightLine }}</p>
              <div
                v-if="branding.footerLinks.length > 0"
                class="flex flex-wrap items-center gap-x-1.5 gap-y-1"
              >
                <span
                  v-for="(link, index) in branding.footerLinks"
                  :key="index"
                  class="inline-flex items-center"
                >
                  <a
                    :href="link.href"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="font-medium text-primary hover:opacity-80 transition-opacity"
                  >
                    {{ link.text }}
                  </a>
                  <span
                    v-if="index < branding.footerLinks.length - 1"
                    class="mx-1.5 text-muted-foreground/30 select-none"
                    aria-hidden="true"
                  >
                    |
                  </span>
                </span>
              </div>
            </div>
          </footer>
        </div>
      </div>
    </div>

    <AppGlobalLoader />

    <!-- Confirmation Dialogs -->
    <AppConfirmDialog
      v-model:is-open="showLeaveImpersonationDialog"
      title="Stop Impersonation?"
      description="Are you sure you want to end this impersonation session and return to your administrator account?"
      confirm-text="End Session"
      type="warning"
      @confirm="confirmLeaveImpersonation"
    />
  </div>
</template>

<style scoped>
/* Main content area transitions */
main {
  transition: padding 0.3s ease;
}
</style>
